import * as Dfns from 'date-fns/fp';
import { WtoRule } from '../wto-form-context';
import {
  hasDuplicates,
  hasNumber,
  isNumeric,
  addInvalidClass,
  removeInvalidClass,
  getFormFieldIndex
} from './validation-helpers';

export interface FormValidatorContext {
  currentUtmTestId?: string;
  currentRulesArray?: WtoRule[];
  currentRuleIndex?: number;
}

export const areMultipleTimeFrameTestIdsTheSame = (rulesArray: any) => {
  if (rulesArray.length <= 1) {
    return { hasDuplicates: false };
  }

  const filteredTimeFrameEnabledRules: any = [];
  const timeFrameTestIds: any = [];

  for (let i = 0; i < rulesArray.length; i++) {
    if (rulesArray[i].timeframe_enabled && rulesArray[i].timeframe.timeframe_utm_test_id_enabled) {
      filteredTimeFrameEnabledRules.push({
        rule_name: rulesArray[i].rule_name,
        utm_test_id: rulesArray[i].timeframe.utm_test_id
      });
    } else {
      filteredTimeFrameEnabledRules.push(null);
    }
  }

  if (filteredTimeFrameEnabledRules.length > 1) {
    for (let i = 0; i < filteredTimeFrameEnabledRules.length; i++) {
      if (filteredTimeFrameEnabledRules[i]) {
        timeFrameTestIds.push(filteredTimeFrameEnabledRules[i].utm_test_id);
      } else {
        timeFrameTestIds.push(null);
      }
    }

    const dupeResultObject = hasDuplicates(timeFrameTestIds);

    if (dupeResultObject.hasDuplicates) {
      return {
        hasDuplicates: true,
        message: `${dupeResultObject.duplicateValue} is already in use for the following rule: ${
          filteredTimeFrameEnabledRules[dupeResultObject.originalIndex].rule_name
        }`
      };
    }
    return { hasDuplicates: false };
  }

  return { hasDuplicates: false };
};

export const validateTimeframeDates = (formObj: any, invalidationArray: any[]) => {
  if (
    formObj.timeframe_enabled &&
    (!formObj.timeframe.startDateTime || !formObj.timeframe.endDateTime)
  ) {
    invalidationArray.push('Time frame requires a start and end date');
    addInvalidClass('ant-picker-range', 'class');
  } else if (
    formObj.timeframe_enabled &&
    formObj.timeframe.startDateTime &&
    formObj.timeframe.endDateTime &&
    (Dfns.isAfter(
      Dfns.parseISO(formObj.timeframe.endDateTime),
      Dfns.parseISO(formObj.timeframe.startDateTime)
    ) ||
      formObj.timeframe.startDateTime === formObj.timeframe.endDateTime)
  ) {
    invalidationArray.push(
      'Time Frame start and end times cannot be the same and start time cannot be ahead of end time'
    );
    addInvalidClass('ant-picker-range', 'class');
  } else {
    const index = getFormFieldIndex(invalidationArray, 'Time frame requires a start and end date');

    if (index !== -1) {
      invalidationArray.splice(index, 1);
      removeInvalidClass('ant-picker-range', 'class');
    }
  }
};

export const validateTimeframeUtmTestId = (
  formObj: any,
  invalidationArray: any[],
  optionalObject?: FormValidatorContext
) => {
  let message: string;

  if (formObj.timeframe_enabled) {
    const areMultTimeFrameIds = areMultipleTimeFrameTestIdsTheSame(
      optionalObject?.currentRulesArray
    );
    // TODO(DEV-24309): requires the string to end at the version digits -- relax to allow a suffix.
    if (
      /^utm_[a-zA-Z-]+_exp=[a-zA-Z-]+[0-9]+$/.test(formObj.timeframe.utm_test_id) &&
      hasNumber(formObj.timeframe.utm_test_id) &&
      optionalObject?.currentUtmTestId !== formObj.timeframe.utm_test_id &&
      !areMultTimeFrameIds.hasDuplicates
    ) {
      const index = getFormFieldIndex(invalidationArray, 'timeframe_utm_test_id');
      if (index !== -1) {
        invalidationArray.splice(index, 1);
      }

      removeInvalidClass('timeframe_utm_test_id-keyname', 'id');
      removeInvalidClass('timeframe_utm_test_id-plovername', 'id');
      removeInvalidClass('timeframe_utm_test_id-versionnumber', 'id');
    } else {
      switch (true) {
        case formObj.timeframe.utm_test_id === '':
          message = 'Time Frame Route Tracking URL Parameter cannot be left blank';
          invalidationArray.push(message);
          addInvalidClass('timeframe_utm_test_id-keyname', 'id');
          addInvalidClass('timeframe_utm_test_id-plovername', 'id');
          addInvalidClass('timeframe_utm_test_id-versionnumber', 'id');
          break;
        case formObj.timeframe.utm_test_id !== '' &&
          formObj.timeframe.utm_test_id.split('utm_')[1].split('_')[0] === '':
        case formObj.timeframe.utm_test_id !== '' &&
          (formObj.timeframe.utm_test_id.split('=')[1] === '' ||
            isNumeric(formObj.timeframe.utm_test_id.split('=')[1])):
        case !hasNumber(formObj.timeframe.utm_test_id):
          if (formObj.timeframe.utm_test_id.split('utm_')[1].split('_')[0] === '') {
            message = 'Time Frame Route Tracking URL Parameter - Funnel Step cannot be left blank';
            invalidationArray.push(message);

            if (
              !document
                .getElementById('timeframe_utm_test_id-keyname')
                ?.classList.contains('invalid')
            ) {
              addInvalidClass('timeframe_utm_test_id-keyname', 'id');
            }
          } else {
            removeInvalidClass('timeframe_utm_test_id-keyname', 'id');
          }

          if (
            formObj.timeframe.utm_test_id.split('=')[1] === '' ||
            isNumeric(formObj.timeframe.utm_test_id.split('=')[1])
          ) {
            message = 'Time Frame Route Tracking URL Parameter - Test Name cannot be left blank';
            invalidationArray.push(message);

            if (
              !document
                .getElementById('timeframe_utm_test_id-plovername')
                ?.classList.contains('invalid')
            ) {
              addInvalidClass('timeframe_utm_test_id-plovername', 'id');
            }
          } else {
            removeInvalidClass('timeframe_utm_test_id-plovername', 'id');
          }

          if (!hasNumber(formObj.timeframe.utm_test_id)) {
            message =
              'Time Frame Route Tracking URL Parameter - Version Number cannot be left blank';
            invalidationArray.push(message);

            if (
              !document
                .getElementById('timeframe_utm_test_id-versionnumber')
                ?.classList.contains('invalid')
            ) {
              addInvalidClass('timeframe_utm_test_id-versionnumber', 'id');
            }
          } else {
            removeInvalidClass('timeframe_utm_test_id-versionnumber', 'id');
          }
          break;
        case optionalObject?.currentUtmTestId === formObj.timeframe.utm_test_id:
          message =
            'Route Tracking URL Parameter cannot have the same value as a Time Frame Route Tracking URL Parameter';
          invalidationArray.push(message);

          if (
            !document.getElementById('timeframe_utm_test_id-keyname')?.classList.contains('invalid')
          ) {
            addInvalidClass('timeframe_utm_test_id-keyname', 'id');
          }

          if (
            !document
              .getElementById('timeframe_utm_test_id-plovername')
              ?.classList.contains('invalid')
          ) {
            addInvalidClass('timeframe_utm_test_id-plovername', 'id');
          }

          if (
            !document
              .getElementById('timeframe_utm_test_id-versionnumber')
              ?.classList.contains('invalid')
          ) {
            addInvalidClass('timeframe_utm_test_id-versionnumber', 'id');
          }
          break;
        case areMultTimeFrameIds.hasDuplicates:
          message = areMultTimeFrameIds.message ? areMultTimeFrameIds.message : '';
          invalidationArray.push(message);

          if (
            !document.getElementById('timeframe_utm_test_id-keyname')?.classList.contains('invalid')
          ) {
            addInvalidClass('timeframe_utm_test_id-keyname', 'id');
          }

          if (
            !document
              .getElementById('timeframe_utm_test_id-plovername')
              ?.classList.contains('invalid')
          ) {
            addInvalidClass('timeframe_utm_test_id-plovername', 'id');
          }

          if (
            !document
              .getElementById('timeframe_utm_test_id-versionnumber')
              ?.classList.contains('invalid')
          ) {
            addInvalidClass('timeframe_utm_test_id-versionnumber', 'id');
          }
          break;
        default:
          message = 'Time Frame Route Tracking URL Parameter is invalid';
          invalidationArray.push(message);

          if (
            !document.getElementById('timeframe_utm_test_id-keyname')?.classList.contains('invalid')
          ) {
            addInvalidClass('timeframe_utm_test_id-keyname', 'id');
          }

          if (
            !document
              .getElementById('timeframe_utm_test_id-plovername')
              ?.classList.contains('invalid')
          ) {
            addInvalidClass('timeframe_utm_test_id-plovername', 'id');
          }

          if (
            !document
              .getElementById('timeframe_utm_test_id-versionnumber')
              ?.classList.contains('invalid')
          ) {
            addInvalidClass('timeframe_utm_test_id-versionnumber', 'id');
          }
      }

      if (
        formObj.timeframe.utm_test_id > 0 &&
        formObj.timeframe.utm_test_id.split('utm_')[1].split('_')[0] === ''
      ) {
        if (
          !document.getElementById('timeframe_utm_test_id-keyname')?.classList.contains('invalid')
        ) {
          addInvalidClass('timeframe_utm_test_id-keyname', 'id');
        }
      }
    }
  }
};
